import { StandardRequest, StandardResponse } from "./functions";
import { ITagAssociationMeta } from "./tag";
import { IEntityMemoryMeta, IEntityMediaMeta, IEntityGroupMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "./entity-links";
import type { MemoryModel } from "./memories";
import type { MediaModel } from "./media";
import type { GroupModel } from "./groups";
import type { PlaceModel } from "./places";
import type { AnchorModel } from "./anchors";
import { IUnifiedFilter } from "./filters";
/**
 * @swagger
 *  components:
 *    schemas:
 *      Contact:
 *        type: object
 *        required: [ "displayName", "isActive", "createdAt", "ownerId" ]
 *        properties:
 *          id:
 *            type: string
 *          email:
 *            type: string
 *          ownerId:
 *            type: string
 *          displayName:
 *            type: string
 *          isActive:
 *            type: boolean
 *          userId:
 *            type: string
 *          groupIds:
 *            type: array
 *            items:
 *              type: string
 *      ContactModel:
 *        allOf:
 *          - $ref: '#/components/schemas/Contact'
 *          - type: object
 *            properties:
 *              userDisplayName:
 *                type: string
 */
/**
 * Raw persisted database document schema for a Contact entity in Firestore (`contacts/{id}`).
 */
export interface IContact {
  id?: string;
  ownerId: string;
  email?: string;
  displayName: string;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number;
  userId?: string;
  /** Foreign key pointing to media.id for custom contact picture */
  photoMediaId?: string;
  /** Biographical notes or description */
  description?: string;
  /** 1-5 star favourite rating */
  favouriteRating?: number;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Companion synchronized array of media IDs maintained for Firestore query indexing */
  mediaIds?: string[];
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of group IDs maintained for Firestore query indexing */
  groupIds?: string[];
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
}

export type Contact = IContact;

import { ShareSummary, SharedToSummary } from "./shares";

export type ContactModel = Contact & {
  groupItems?: GroupModel[];
  memoryItems?: MemoryModel[];
  mediaItems?: MediaModel[];
  placeItems?: PlaceModel[];
  anchorItems?: AnchorModel[];
  userRole?: "owner" | "editor" | "viewer";
  canShare?: boolean;
  sharedWith?: ShareSummary[];
  sharedTo?: SharedToSummary[];
  ownerDisplayName?: string;
  /**
   * Transient profile photo dynamically resolved from the linked user's profile by email or userId.
   * Not stored directly on the contact document.
   */
  photoUrl?: string;
  /**
   * Transient 50x50 profile icon dynamically resolved from the linked user's profile by email or userId.
   * Not stored directly on the contact document.
   */
  photoIconUrl?: string;
  /**
   * Registered display name of the linked AppUser (when contact is linked via userId or email).
   * Not stored directly on the contact document.
   */
  userDisplayName?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyContactsRequest:
 *        type: object
 *        properties:
 *          includeInactive:
 *            type: boolean
 */
export type GetMyContactsRequest = {
  includeInactive?: boolean;
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyContactsResponse:
 *        type: object
 *        required: [ "status", "timestamp", "contacts" ]
 *        properties:
 *          status:
 *            type: boolean
 *          timestamp:
 *            type: number
 *          contacts:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ContactModel'
 *          error:
 *            type: string
 */
export type GetMyContactsResponse = {
  status: boolean;
  timestamp: number;
  contacts: ContactModel[];
  error?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      EnsureAllContactsGroupRequest:
 *        type: object
 *        required: [ "ownerId" ]
 *        properties:
 *          ownerId:
 *            type: string
 */
export type EnsureAllContactsGroupRequest = {
  ownerId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      AddContactRequest:
 *        type: object
 *        required: [ "displayName" ]
 *        properties:
 *          email:
 *            type: string
 *          displayName:
 *            type: string
 */
export type AddContactRequest = {
  email?: string;
  displayName: string;
  groupIds?: string[];
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Foreign key pointing to media.id for custom contact picture */
  photoMediaId?: string;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Biographical notes or description */
  description?: string;
  /** 1-5 star favourite rating */
  favouriteRating?: number;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      AddContactResponse:
 *        type: object
 *        properties:
 *          contact:
 *            $ref: '#/components/schemas/ContactModel'
 */
export type AddContactResponse = StandardResponse & {
  contact?: ContactModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllContactsRequest:
 *        type: object
 *        properties: {}
 */
export type GetAllContactsRequest = {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllContactsResponse:
 *        type: object
 *        required: [ "status", "timestamp", "contacts" ]
 *        properties:
 *          status:
 *            type: boolean
 *          timestamp:
 *            type: number
 *          contacts:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ContactModel'
 *          error:
 *            type: string
 */
export type GetAllContactsResponse = {
  status: boolean;
  timestamp: number;
  contacts: ContactModel[];
  error?: string;
};

export type DeleteContactRequest = {
  contactId: string;
  cascadeUnlink?: boolean;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      EditContactRequest:
 *        type: object
 *        required: [ "contactId" ]
 *        properties:
 *          contactId:
 *            type: string
 *          displayName:
 *            type: string
 *          email:
 *            type: string
 *          groupIds:
 *            type: array
 *            items:
 *              type: string
 */
export type EditContactRequest = {
  contactId: string;
  displayName?: string;
  email?: string;
  groupIds?: string[];
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Foreign key pointing to media.id for custom contact picture */
  photoMediaId?: string;
  /** Biographical notes or description */
  description?: string;
  /** 1-5 star favourite rating */
  favouriteRating?: number;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      ContactFilter:
 *        type: object
 *        properties:
 *          name:
 *            type: string
 *            description: Regex pattern to filter contacts by display name
 *          email:
 *            type: string
 *            description: Regex pattern to filter contacts by email
 */
export type ContactFilter = {
  name?: string;
  email?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetContactsRequest:
 *        type: object
 *        properties:
 *          filter:
 *            $ref: '#/components/schemas/ContactFilter'
 */
export type GetContactsRequest = {
  filter?: ContactFilter | IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetContactsResponse:
 *        type: object
 *        required: [ "status", "contacts" ]
 *        properties:
 *          status:
 *            type: boolean
 *          contacts:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ContactModel'
 *          error:
 *            type: string
 */
export type GetContactsResponse = {
  status: boolean;
  contacts: ContactModel[];
  error?: string;
};

export type InviteContactRequest = {
  contactId: string;
  domain?: string;
};

export type InviteContactResponse = {
  status: boolean;
  timestamp: number;
  error?: string;
  message?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyContactRequest:
 *        type: object
 *        required: [ "contactId" ]
 *        properties:
 *          contactId:
 *            type: string
 */
export type GetMyContactRequest = StandardRequest & {
  contactId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetContactRequest:
 *        type: object
 *        required: [ "contactId" ]
 *        properties:
 *          contactId:
 *            type: string
 */
export type GetContactRequest = GetMyContactRequest;

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyContactResponse:
 *        type: object
 *        properties:
 *          contact:
 *            $ref: '#/components/schemas/ContactModel'
 */
export type GetMyContactResponse = StandardResponse & {
  contact?: ContactModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetContactResponse:
 *        type: object
 *        properties:
 *          contact:
 *            $ref: '#/components/schemas/ContactModel'
 */
export type GetContactResponse = GetMyContactResponse;

/**
 * @swagger
 *  components:
 *    schemas:
 *      UnlinkContactRequest:
 *        type: object
 *        required: [ "contactId" ]
 *        properties:
 *          contactId:
 *            type: string
 */
export type UnlinkContactRequest = StandardRequest & {
  contactId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      UnlinkContactResponse:
 *        type: object
 *        required: [ "status" ]
 *        properties:
 *          status:
 *            type: boolean
 *          error:
 *            type: string
 *          timestamp:
 *            type: number
 */
export type UnlinkContactResponse = StandardResponse;
